/**
 * nuggets.ts — golden nuggets: a random clickable bonus that spawns over the
 * mine. The spawn/expiry lifecycle is a PURE tick system driven by the seeded
 * rng + gameTimeMs (appended at the END of tickGame so the rng stream order is
 * stable and determinism holds). Claiming is a UI-triggered action that funnels
 * the reward through the economy / buff stack — and never touches the rng, so it
 * stays deterministic regardless of when the player clicks.
 */
import type { GameState } from '../core/GameState';
import type { EventBus } from '../core/EventBus';
import { next, range } from '../core/rng';
import { safeNumber, format } from '../core/numbers';
import { BALANCE, NUGGET_REWARDS, NUGGET_BY_ID, type NuggetDef } from '../data';
import { addModifier } from './modifiers';
import { computeDerived } from './derived';
import { unitPrice } from './economy';

function weightedPick(state: GameState): NuggetDef {
  const total = NUGGET_REWARDS.reduce((sum, n) => sum + n.weight, 0);
  let roll = next(state.rng) * total;
  for (const n of NUGGET_REWARDS) {
    roll -= n.weight;
    if (roll <= 0) return n;
  }
  return NUGGET_REWARDS[NUGGET_REWARDS.length - 1]!;
}

/** Pure spawn/expiry tick. Draws zero rng until nuggets are unlocked. */
export function tickNuggets(state: GameState, bus?: EventBus): void {
  if (state.stats.lifetimeEarnings < BALANCE.nuggets.unlockLifetime) return;
  const now = state.meta.gameTimeMs;
  const cfg = BALANCE.nuggets;

  if (state.nugget.active) {
    if (now >= state.nugget.expiresAt) {
      state.nugget.active = false;
      state.nugget.nextSpawnAt = now + range(state.rng, cfg.minGap, cfg.maxGap) * 1000;
      bus?.emit('nugget:expired', {});
    }
    return;
  }

  if (now < state.nugget.nextSpawnAt) return;
  if (next(state.rng) >= cfg.chance) {
    state.nugget.nextSpawnAt = now + cfg.rollInterval * 1000;
    return;
  }

  const def = weightedPick(state);
  state.nugget = {
    active: true,
    kind: def.id,
    x: range(state.rng, 0.08, 0.92),
    y: range(state.rng, 0.12, 0.88),
    spawnedAt: now,
    expiresAt: now + cfg.ttl * 1000,
    nextSpawnAt: 0,
  };
  bus?.emit('nugget:spawned', { kind: def.id });
}

/** Estimated current income per second (for sizing the cash reward). */
function incomePerSec(state: GameState): number {
  const d = computeDerived(state);
  return d.miningRate * unitPrice(state, d.focusOreId, d);
}

/** Claim the active nugget. Returns a short reward description, or '' if none. */
export function claimNugget(state: GameState, bus?: EventBus): string {
  if (!state.nugget.active) return '';
  const def = NUGGET_BY_ID[state.nugget.kind];
  const now = state.meta.gameTimeMs;
  let desc = '';

  if (!def) {
    state.nugget.active = false;
    return '';
  }

  if (def.kind === 'cash') {
    const reward = Math.max(
      50,
      BALANCE.nuggets.cashFactor * incomePerSec(state),
      state.economy.money * 0.1,
    );
    const amt = safeNumber(reward);
    state.economy.money += amt;
    state.stats.lifetimeEarnings += amt;
    desc = `+$${format(amt)}`;
  } else if (def.kind === 'ore') {
    const d = computeDerived(state);
    const oreId = d.focusOreId;
    const amount = safeNumber(Math.max(50, d.miningRate * 120 + 50));
    const have = state.mining.ore[oreId] ?? 0;
    state.mining.ore[oreId] = Math.min(have + amount, d.storage);
    const added = state.mining.ore[oreId] - have;
    state.stats.totalOreMined += added;
    state.stats.oreMined[oreId] = (state.stats.oreMined[oreId] ?? 0) + added;
    desc = `+${format(added)} ore`;
  } else if (def.kind === 'buff' && def.effect) {
    addModifier(state, {
      id: `nugget:${def.id}`,
      label: def.name,
      effect: def.effect,
      endsAt: now + (def.duration ?? 20) * 1000,
    });
    desc = def.name;
  }

  state.nugget.active = false;
  // Fixed gap (no rng) so claiming stays deterministic relative to the tick stream.
  state.nugget.nextSpawnAt = now + BALANCE.nuggets.minGap * 1000;
  state.stats.nuggetsClicked += 1;
  bus?.emit('nugget:claimed', { kind: def.id, reward: desc });
  bus?.emit('notify', { kind: 'reward', message: `${def.icon} ${def.name}: ${desc}` });
  return desc;
}
